/**
 * Tomadores e a ficha completa do tomador.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import type { CreditAnalysis } from './analysis';
import type { BankAccount } from './brokers';
import { query, type Address, type ListParams, type Money } from './comum';
import type { DocumentDeletion } from './documents';

import { api, requestBlob, requestPage } from '../client';

export interface Policyholder {
  id: string;
  number: number;
  legal_name: string;
  trade_name: string;
  document: string;
  state_registration: string;
  status: string;
  email: string;
  phone: string;
  contact_name: string;
  address: Address;
  credit_limit: Money;
  notes: string;
  company_size: string;
  legal_nature: string;
  cnae: string;
  share_capital: Money;
  founded_on: string | null;
  broker_id: string | null;
  commercial_owner: string;
  created_at: string;
  updated_at: string;

  /**
   * Corretor vinculado com o nome, para quem le o tomador sem ler corretores. Os
   * campos abaixo sao a ficha completa (paridade com o OnPoint, ADR 0021); vem
   * opcionais para que respostas antigas e simulacoes de teste continuem validas.
   */
  broker?: { id: string; label: string } | null;
  registration_valid_until?: string | null;
  reregistered_on?: string | null;
  commission_bps?: number | null;
  nomination_valid_until?: string | null;
  nomination_instrument?: string;
  foundation_note?: string;
  registration_situation?: string;
  cnae_description?: string;
  secondary_cnaes?: string[];
  branch_activity?: string;
  branch_activity_restricted?: boolean | null;
  credit_status?: string;
  blocked_at?: string | null;
  block_reason?: string;
  ignore_ccg_signature?: boolean;
  blocked_by_restrictive?: boolean;
  enable_annual_installments?: boolean;
  limit_automatic_review?: boolean;
  payment_equivalent_class?: string;
  force_approved_dirty_list?: boolean;
  last_claims_evaluation_on?: string | null;
  annual_billing_percentage_bps?: number | null;
  ccg_custom_clause?: string;
  ccg_uses_spouse?: boolean;

  /**
   * Tomador complementar (P-TOM-C1): pessoa fisica ou juridica, marcas da carga e
   * negativa de credito. Opcionais pelo mesmo motivo dos campos da ficha.
   */
  document_kind?: DocumentKind;
  is_private?: boolean | null;
  document_invalid?: boolean;
  import_source?: string;
  credit_denial_reason?: string;
  credit_denial_reason_id?: string | null;
}

/** Pessoa fisica (CPF) ou juridica (CNPJ), o "CPF/CNPJ" do OnPoint. */
export type DocumentKind = 'cpf' | 'cnpj';

/** Campos da ficha completa que a edicao do cadastro envia. */
export interface PolicyholderRecordInput {
  registration_valid_until?: string;
  reregistered_on?: string;
  commission_bps?: number | null;
  nomination_valid_until?: string;
  nomination_instrument?: string;
  foundation_note?: string;
  registration_situation?: string;
  cnae_description?: string;
  secondary_cnaes?: string[];
  branch_activity?: string;
  branch_activity_restricted?: boolean | null;
  credit_status?: string;
  enable_annual_installments?: boolean;
  limit_automatic_review?: boolean;
  payment_equivalent_class?: string;
  force_approved_dirty_list?: boolean;
  last_claims_evaluation_on?: string;
  annual_billing_percentage_bps?: number | null;
  ccg_custom_clause?: string;
  ccg_uses_spouse?: boolean;
  is_private?: boolean | null;
  credit_denial_reason?: string;
  credit_denial_reason_id?: string | null;
}

export interface PolicyholderInput extends PolicyholderRecordInput {
  legal_name?: string;
  trade_name?: string;
  document?: string;
  document_kind?: DocumentKind;
  state_registration?: string;
  status?: string;
  email?: string;
  phone?: string;
  contact_name?: string;
  address?: Address;
  credit_limit?: Money;
  /** Valor que a tela abriu. Diferente do gravado, o servidor recusa com 409. */
  credit_limit_base?: Money;
  credit_status_base?: string;
  notes?: string;
  company_size?: string;
  legal_nature?: string;
  cnae?: string;
  share_capital?: Money;
  founded_on?: string;
  broker_id?: string;
  commercial_owner?: string;
}

export const policyholders = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<Policyholder>(`/api/v1/policyholders${query(params)}`, signal),
  get: (id: string, signal?: AbortSignal) =>
    api.get<Policyholder>(`/api/v1/policyholders/${id}`, signal),
  create: (input: PolicyholderInput) =>
    api.post<Policyholder>('/api/v1/policyholders', input),
  update: (id: string, input: PolicyholderInput) =>
    api.patch<Policyholder>(`/api/v1/policyholders/${id}`, input),
  remove: (id: string) => api.delete<void>(`/api/v1/policyholders/${id}`),
  /** Lista inteira do filtro, para o "Exportar dados". Exige a permissao de exportar. */
  exportAll: (params: ListParams) =>
    api.get<Policyholder[]>(`/api/v1/policyholders/export${query(params)}`),
  block: (id: string, reason: string) =>
    api.post<Policyholder>(`/api/v1/policyholders/${id}/block`, { reason }),
  setCcgWaiver: (id: string, ignore: boolean) =>
    api.put<Policyholder>(`/api/v1/policyholders/${id}/ccg-waiver`, {
      ignore_ccg_signature: ignore,
    }),
  setRestrictiveBlock: (id: string, blocked: boolean) =>
    api.put<Policyholder>(`/api/v1/policyholders/${id}/restrictive-block`, {
      blocked_by_restrictive: blocked,
    }),
};

// --- Ficha completa do tomador --------------------------------------------

/** Perfil de credito e restritivos. Nulo e "nao informado", e nunca zero. */
/** De onde veio um campo do perfil que o automatico preencheu (ADR 0101). */
export interface CreditProfileFieldOrigin {
  source: string;
  reference_date?: string;
  filled_at?: string;
}

export interface CreditProfile {
  credit_size: string;
  financial_risk_class: string;
  final_score: number | null;
  score: number | null;
  risk_score: string;
  special_situation: string;
  bureau_name: string;
  restrictive_consulted_on: string | null;
  presumed_revenue: Money | null;
  payment_capacity: Money | null;
  max_payment: Money | null;
  bureau_limit: Money | null;
  wiz_max_limit: Money | null;
  cp_max_limit: Money | null;
  wiz_bonus: Money | null;
  pefin: Money | null;
  pefin_debit: Money | null;
  refin: Money | null;
  protest: Money | null;
  overdue_debt: Money | null;
  pefin_percentage_bps: number | null;
  debit_percentage_bps: number | null;
  protest_percentage_bps: number | null;
  ccf_count: number | null;
  restrictive_points: number | null;
  reviewed_payment_capacity: Money | null;
  actual_revenue: Money | null;
  reviewed_size: string;
  reviewed_limit: Money | null;
  reviewed_on: string | null;
  review_notes: string;
  /** Campos preenchidos pela consulta ou pelo motor. Fora do mapa: digitado ou em branco. */
  field_origins?: Record<string, CreditProfileFieldOrigin>;
  updated_label?: string;
  updated_at?: string | null;
}

export interface PolicyholderBranch {
  id: string;
  document: string;
  legal_name: string;
  address: Address;
  created_label: string;
  created_at: string;
}

export interface PolicyholderBankAccount {
  id: string;
  payment_method: string;
  /** Meio da lista da empresa (P-TOM-C1). Nulo nas contas digitadas antes. */
  payment_method_id?: string | null;
  bank_account: BankAccount;
  created_label: string;
  created_at: string;
}

export interface PolicyholderObservation {
  id: string;
  body: string;
  author_label: string;
  created_at: string;
}

export interface CommercialTerm {
  id: string;
  product_id: string;
  modality_id: string;
  kind: 'premio_fixo' | 'premio_minimo';
  amount: Money;
  created_label: string;
  created_at: string;
  /** Ultima edicao (TOM-931): vazio e nulo ate a primeira. */
  updated_label: string;
  updated_at: string | null;
}

/** Edicao da condicao: campo ausente fica como esta. A modalidade nao muda. */
export interface CommercialTermChange {
  kind?: CommercialTerm['kind'];
  amount?: Money;
}

export type CCGRole = 'representante_legal' | 'fiador_pf' | 'fiador_pj';

export interface CCGParty {
  id: string;
  role: CCGRole;
  document: string;
  name: string;
  email: string;
  phone: string;
  nationality: string;
  marital_status: string;
  profession: string;
  identity_document: string;
  address: Address;
  representative_document: string;
  representative_name: string;
  representative_email: string;
  spouse_name: string;
  spouse_document: string;
  created_label: string;
  created_at: string;
}

export type CCGPartyInput = Partial<
  Omit<CCGParty, 'id' | 'created_label' | 'created_at'>
> & {
  role: CCGRole;
  document: string;
};

export type PartnerRole = 'socio' | 'administrador' | 'representante';

export interface Partner {
  id: string;
  parent_id: string | null;
  role: PartnerRole;
  role_description: string;
  name: string;
  document: string;
  document_kind: 'cpf' | 'cnpj';
  participation_bps: number | null;
  is_pep: boolean;
  is_majority: boolean;
  /**
   * Motivo de ser PEP (PEPReason do OnPoint, ESC-1015). So vale com o PEP marcado: o
   * servidor o tira quando o PEP sai. Nao decide nada (CAD-015 pendente).
   */
  pep_reason?: string;
  /** Marcas do OnPoint (P-TOM-C1). A de documento invalido so a carga preenche. */
  is_private?: boolean | null;
  document_invalid?: boolean;
  created_label: string;
  created_at: string;
}

export type PartnerInput = Partial<
  Omit<Partner, 'id' | 'created_label' | 'created_at' | 'document_invalid'>
>;

export interface EconomicGroup {
  id: string;
  group_document: string;
  group_name: string;
  participation_bps: number;
  afiancado_holding: boolean;
  group_limit: Money | null;
  /** O grupo como entidade (P-TOM-C5), ligado pelo banco a partir do documento. */
  economic_group_id?: string | null;
  created_label: string;
  created_at: string;
  updated_at: string;
}

export interface EconomicGroupInput {
  group_document?: string;
  group_name?: string;
  participation_bps?: number;
  afiancado_holding?: boolean;
  group_limit?: Money | null;
}

export interface CreditLimitGroup {
  modality_group: string;
  /** Limite concedido, por uma pessoa ou pelo motor; so com o calculado, fica falso. */
  configured: boolean;
  approved: Money;
  used: Money;
  available: Money;
  public_rate_bps: number | null;
  private_rate_bps: number | null;
  updated_label: string;
  updated_at: string | null;
  /** P-TOM-C2: o que o motor calculou. Nulo para a corretora (PM-09 pendente). */
  changed_manually: boolean;
  engine_limit: Money | null;
  calculated_limit: Money | null;
  payment_capacity: Money | null;
  rule_execution_id: string | null;
  /** C-LIM: validade do limite aprovado (AAAA-MM-DD) e a ultima mudanca do aprovado. */
  valid_until?: string | null;
  approved_at?: string | null;
  /** PM-09: limite e taxa ocultos a corretora com cartao de credito ou PEP aberto. */
  hidden_from_broker?: boolean;
  /** Quem aprovou: automatico (motor) ou manual, com o nome. Vazio para a corretora. */
  approved_origin?: 'motor' | 'manual' | '';
  approved_label?: string;
}

export interface PolicyholderLimitInput {
  approved: Money;
  used: Money;
  public_rate_bps: number | null;
  private_rate_bps: number | null;
  /** C-LIM: validade do limite; vazio ou nulo, sem data. */
  valid_until?: string | null;
  /** Motivo da mudanca: obrigatorio quando o aprovado muda. */
  reason?: string;
}

/** Linha do historico do tomador, com o antes e o depois gravados na trilha. */
export interface ChangeEntry {
  id: string;
  action: string;
  actor_label: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  occurred_at: string;
}

export interface PolicyholderCreditAnalysis extends CreditAnalysis {
  operation: {
    id: string;
    number: number;
    stage: string;
    subject: string;
    created_at: string;
  };
}

export interface EngineRunSummary {
  id: string;
  /** Nulo na execucao do tomador, sem operacao (run_kind tomador, ADR 0090). */
  operation_id: string | null;
  policyholder_id: string;
  engine_version: string;
  mode: string;
  reference_at: string;
  final_result: string;
  calculated_result: string;
  pendency_count: number;
  simulated: boolean;
  notice?: string;
  requested_label: string;
  created_at: string;
}

export interface CreditConsultation {
  id: string;
  policyholder_id: string;
  provider: string;
  simulated: boolean;
  notice?: string;
  status: string;
  reused: boolean;
  /** A consulta foi reaproveitada e o motor reavaliou as regras, sem custo. */
  reanalyzed?: boolean;
  consulted_at: string;
  valid_until: string;
  valid: boolean;
  requested_label: string;
  created_at: string;
  /** Etapa (basica ou master), produto e ambiente da consulta (C-VGD). */
  stage?: string;
  product?: string;
  environment?: string;
  /** O que a leitura trouxe, um valor por elemento. */
  values?: CreditConsultationValue[];
}

/** Um valor da leitura da consulta, com o tipo do elemento. */
export interface CreditConsultationValue {
  element: string;
  label: string;
  situation: string;
  value_text: string | null;
  value_money: Money | null;
  value_date: string | null;
  value_flag: boolean | null;
  value_integer: number | null;
  model?: string;
  reference_date: string | null;
}

/** Regra na execucao do motor do tomador. */
export interface PolicyholderRunRule {
  rule: string;
  name: string;
  /** Coluna da matriz de decisao: S, V, A, E ou vazia. */
  column?: string;
  effective: string;
  calculated: string;
  message: string;
  recommendation?: string;
  lane?: string;
  homologated: boolean;
  shadow: boolean;
}

/** A ultima execucao do motor do tomador, sem operacao (ADR 0090). */
export interface PolicyholderEngineRun extends EngineRunSummary {
  run_kind: string;
  parameter_set: {
    id: string;
    version: number;
    status_at_run: string;
    values_hash: string;
  };
  rules_with_effect: PolicyholderRunRule[];
  /** As regras que seguem. */
  rules_following?: PolicyholderRunRule[];
  following_rules: number;
  not_applicable: number;
  pendencies: {
    rule: string;
    outcome: string;
    message: string;
    recommendation?: string;
  }[];
  master_required: boolean;
  /** A execucao leu uma consulta Master dentro da validade. */
  master_used?: boolean;
}

/** Se o dado e bom ou ruim por si. Sem tom, quem julga sao as regras. */
export type ConsultationDataTone = 'positivo' | 'negativo';

/** Uma linha de um quadro da consulta, ja escrita pelo servidor. */
export interface ConsultationDataRow {
  label: string;
  value: string;
  hint?: string;
  tone?: ConsultationDataTone;
}

/** Uma lista item a item dentro de um quadro da consulta. */
export interface ConsultationDataTable {
  title: string;
  columns: string[];
  rows: string[][];
}

/** Um quadro da consulta, o mesmo na tela e no arquivo da analise (ADR 0099). */
export interface ConsultationDataSection {
  key: string;
  title: string;
  tone?: ConsultationDataTone;
  /** Diz por que o quadro esta vazio. */
  empty?: string;
  rows: ConsultationDataRow[];
  tables: ConsultationDataTable[];
}

/** A decisao gravada com a execucao do tomador. */
export interface PolicyholderEngineDecision {
  approve: boolean;
  note: string;
  reasons: { code: string; message: string }[];
  /** aprovado, esteira, anexado, analise_desligada ou sem_cadastro. */
  result: string;
  review_id?: string;
  card_opened: boolean;
  credit_status_changed: boolean;
  held_back: string;
  actor: string;
  /** A lista aprovacao_automatica_regras usada; vazia, decidiu o resultado inteiro. */
  rules?: string[];
  /** Pendencias das regras fora da lista: registradas, nao decidiram. */
  undecided?: {
    rule: string;
    outcome: string;
    message: string;
    recommendation?: string;
  }[];
}

/** Faixa de classe da versao ativa, inclusiva nas duas pontas (`crd003_faixas`). */
export interface ScoreBand {
  class: string;
  min: number;
  max: number;
}

/**
 * Consulta no cadastro do tomador (ADR 0102). A mesma resposta para a seguradora e para a
 * corretora: so o resultado, sem score, restricao, motivo nem regra.
 */
export interface PolicyholderCreditCheck {
  id: string;
  policyholder_id: string;
  status: 'em_andamento' | 'concluida' | 'interrompida';
  result: 'aprovado' | 'analise_manual' | 'erro' | null;
  /** So na aprovacao. Nulo para a corretora enquanto o limite estiver oculto (PM-09). */
  credit_limit: Money | null;
  started_at: string;
  finished_at: string | null;
}

/** Analise automatica do tomador: a ultima consulta, a execucao e a decisao. */
export interface PolicyholderAutomaticAnalysis {
  consultation: CreditConsultation | null;
  master: CreditConsultation | null;
  run: PolicyholderEngineRun | null;
  decision: PolicyholderEngineDecision | null;
  auto_approval_enabled: boolean;
  /** As regras que decidem a aprovacao automatica na versao ativa (vazia: todas). */
  auto_approval_rules?: string[];
  active_version: boolean;
  /** As faixas de classe da versao ativa da empresa (vazia sem versao ativa). */
  score_bands?: ScoreBand[];
  /** O elemento do score que decide a classe (vazio fora da SeguraCoreCredit). */
  score_element?: string;
  /** Tudo o que a consulta devolveu, organizado por assunto. */
  data_sections?: ConsultationDataSection[];
}

function fichaDo(id: string, resto: string): string {
  return `/api/v1/policyholders/${id}${resto}`;
}

/** Secoes do detalhe do tomador. */
export const policyholderFile = {
  creditProfile: (id: string, signal?: AbortSignal) =>
    api.get<CreditProfile>(fichaDo(id, '/credit-profile'), signal),
  saveCreditProfile: (id: string, input: CreditProfile & { typed_fields?: string[] }) =>
    api.put<CreditProfile>(fichaDo(id, '/credit-profile'), input),

  branches: (id: string, signal?: AbortSignal) =>
    api.get<PolicyholderBranch[]>(fichaDo(id, '/branches'), signal),
  addBranch: (
    id: string,
    input: { document: string; legal_name: string; address: Address },
  ) => api.post<PolicyholderBranch>(fichaDo(id, '/branches'), input),
  removeBranch: (id: string, branchId: string) =>
    api.delete<void>(fichaDo(id, `/branches/${branchId}`)),

  bankAccounts: (id: string, signal?: AbortSignal) =>
    api.get<PolicyholderBankAccount[]>(fichaDo(id, '/bank-accounts'), signal),
  addBankAccount: (
    id: string,
    input: {
      payment_method: string;
      payment_method_id?: string | null;
      bank_account: BankAccount;
    },
  ) => api.post<PolicyholderBankAccount>(fichaDo(id, '/bank-accounts'), input),
  removeBankAccount: (id: string, accountId: string) =>
    api.delete<void>(fichaDo(id, `/bank-accounts/${accountId}`)),

  observations: (id: string, search: string, signal?: AbortSignal) =>
    api.get<PolicyholderObservation[]>(
      fichaDo(id, `/observations${query({ q: search })}`),
      signal,
    ),
  addObservation: (id: string, body: string) =>
    api.post<PolicyholderObservation>(fichaDo(id, '/observations'), { body }),

  commercialTerms: (id: string, signal?: AbortSignal) =>
    api.get<CommercialTerm[]>(fichaDo(id, '/commercial-terms'), signal),
  addCommercialTerms: (
    id: string,
    input: { modality_id: string; fixed_premium?: Money; minimum_premium?: Money },
  ) => api.post<CommercialTerm[]>(fichaDo(id, '/commercial-terms'), input),
  updateCommercialTerm: (id: string, termId: string, input: CommercialTermChange) =>
    api.patch<CommercialTerm>(fichaDo(id, `/commercial-terms/${termId}`), input),
  removeCommercialTerm: (id: string, termId: string) =>
    api.delete<void>(fichaDo(id, `/commercial-terms/${termId}`)),

  ccgParties: (id: string, signal?: AbortSignal) =>
    api.get<CCGParty[]>(fichaDo(id, '/ccg-parties'), signal),
  addCcgParty: (id: string, input: CCGPartyInput) =>
    api.post<CCGParty>(fichaDo(id, '/ccg-parties'), input),
  removeCcgParty: (id: string, partyId: string) =>
    api.delete<void>(fichaDo(id, `/ccg-parties/${partyId}`)),

  partners: (id: string, signal?: AbortSignal) =>
    api.get<Partner[]>(fichaDo(id, '/partners'), signal),
  addPartner: (id: string, input: PartnerInput) =>
    api.post<Partner>(fichaDo(id, '/partners'), input),
  updatePartner: (id: string, partnerId: string, input: PartnerInput) =>
    api.patch<Partner>(fichaDo(id, `/partners/${partnerId}`), input),
  removePartner: (id: string, partnerId: string) =>
    api.delete<void>(fichaDo(id, `/partners/${partnerId}`)),

  economicGroups: (id: string, signal?: AbortSignal) =>
    api.get<EconomicGroup[]>(fichaDo(id, '/economic-groups'), signal),
  addEconomicGroup: (id: string, input: EconomicGroupInput) =>
    api.post<EconomicGroup>(fichaDo(id, '/economic-groups'), input),
  updateEconomicGroup: (id: string, groupId: string, input: EconomicGroupInput) =>
    api.patch<EconomicGroup>(fichaDo(id, `/economic-groups/${groupId}`), input),
  removeEconomicGroup: (id: string, groupId: string) =>
    api.delete<void>(fichaDo(id, `/economic-groups/${groupId}`)),

  creditLimits: (id: string, signal?: AbortSignal) =>
    api.get<CreditLimitGroup[]>(fichaDo(id, '/credit-limits'), signal),
  saveCreditLimit: (id: string, group: string, input: PolicyholderLimitInput) =>
    api.put<CreditLimitGroup>(fichaDo(id, `/credit-limits/${group}`), input),
  limitHistory: (id: string, signal?: AbortSignal) =>
    api.get<LimitHistoryEntry[]>(fichaDo(id, '/credit-limits/history'), signal),

  changeHistory: (id: string, signal?: AbortSignal) =>
    api.get<ChangeEntry[]>(fichaDo(id, '/change-history'), signal),

  creditAnalyses: (id: string, signal?: AbortSignal) =>
    api.get<PolicyholderCreditAnalysis[]>(fichaDo(id, '/credit-analyses'), signal),
  engineRuns: (id: string, signal?: AbortSignal) =>
    api.get<EngineRunSummary[]>(fichaDo(id, '/credit-engine/runs'), signal),
  consultations: (id: string, signal?: AbortSignal) =>
    api.get<CreditConsultation[]>(fichaDo(id, '/credit-consultations'), signal),
  automaticAnalysis: (id: string, signal?: AbortSignal) =>
    api.get<PolicyholderAutomaticAnalysis>(
      fichaDo(id, '/automatic-credit-analysis'),
      signal,
    ),
  automaticAnalysisFile: (id: string) =>
    requestBlob(fichaDo(id, '/automatic-credit-analysis/file')),
  requestConsultation: (id: string, confirmCost: boolean) =>
    api.post<CreditConsultation>(fichaDo(id, '/credit-consultations'), {
      confirm_cost: confirmCost,
    }),

  // Consulta no cadastro (ADR 0102): abre e acompanha, e so devolve o resultado.
  startCreditCheck: (id: string, confirmCost: boolean) =>
    api.post<PolicyholderCreditCheck>(fichaDo(id, '/credit-checks'), {
      confirm_cost: confirmCost,
    }),
  creditCheck: (id: string, checkId: string, signal?: AbortSignal) =>
    api.get<PolicyholderCreditCheck>(
      fichaDo(id, `/credit-checks/${encodeURIComponent(checkId)}`),
      signal,
    ),

  deletedDocuments: (id: string, signal?: AbortSignal) =>
    api.get<DocumentDeletion[]>(
      `/api/v1/documents/deletions${query({ owner_type: 'tomador', owner_id: id })}`,
      signal,
    ),
};

// --- Tomador complementar (P-TOM-C1) ------------------------------------------

/** Resultado da conferencia do CPF ou CNPJ antes do cadastro. */
export interface DocumentCheck {
  document: string;
  document_kind: DocumentKind;
  valid: boolean;
  message: string;
  individual_enabled: boolean;
  registered: boolean;
  policyholder: { id: string; label: string } | null;
  nominated_to_this_broker: boolean;
  nominated_to_other_broker: boolean;
  accepts_new_operations: boolean | null;
}

/** Grupo de credito do limite, com as modalidades da empresa nele. */
export interface CreditGroupInfo {
  modality_group: string;
  /** Descricao dos grupos de modalidades do grupo de credito (TOM-122). Vazia sem descricao. */
  description: string;
  modalities: string[];
}

export interface PolicyholderTradeName {
  id: string;
  name: string;
  is_main: boolean;
  created_label: string;
  created_at: string;
  updated_at: string;
}

export interface PolicyholderAddressEntry {
  id: string;
  sequence: number;
  is_main: boolean;
  is_active: boolean;
  is_international: boolean;
  country_code: string;
  address: Address;
  created_label: string;
  created_at: string;
  updated_at: string;
}

export interface PolicyholderAddressInput {
  address?: Address;
  is_main?: boolean;
  is_active?: boolean;
  is_international?: boolean;
  country_code?: string;
}

export interface PolicyholderContact {
  id: string;
  phone: string;
  is_main: boolean;
  created_label: string;
  created_at: string;
  updated_at: string;
}

/**
 * Sub-aba "Dados cadastrais" do grupo economico, pela participacao. O grupo inteiro vem
 * em `economic_group` (P-TOM-C5). Concedido, utilizado e disponivel do grupo saem nulos
 * ate a metrica de consumo do grupo ser aprovada (LIM-005, CRE-06).
 */
export interface EconomicGroupDetail extends EconomicGroup {
  economic_group?: EconomicGroupEntity | null;
  granted_limit: Money | null;
  used_limit: Money | null;
  available_limit: Money | null;
  policyholder_used_limit?: Money | null;
  pending_rule: string;
  members: EconomicGroupMemberRow[];
}

/** Rotas do tomador complementar. */
export const policyholderComplement = {
  validate: (document: string, signal?: AbortSignal) =>
    api.get<DocumentCheck>(
      `/api/v1/policyholders/validation${query({ document })}`,
      signal,
    ),
  creditGroups: (signal?: AbortSignal) =>
    api.get<CreditGroupInfo[]>('/api/v1/policyholders/credit-groups', signal),

  tradeNames: (id: string, signal?: AbortSignal) =>
    api.get<PolicyholderTradeName[]>(fichaDo(id, '/trade-names'), signal),
  addTradeName: (id: string, input: { name: string; is_main?: boolean }) =>
    api.post<PolicyholderTradeName>(fichaDo(id, '/trade-names'), input),
  updateTradeName: (
    id: string,
    itemId: string,
    input: { name?: string; is_main?: boolean },
  ) => api.patch<PolicyholderTradeName>(fichaDo(id, `/trade-names/${itemId}`), input),
  removeTradeName: (id: string, itemId: string) =>
    api.delete<void>(fichaDo(id, `/trade-names/${itemId}`)),

  addresses: (id: string, signal?: AbortSignal) =>
    api.get<PolicyholderAddressEntry[]>(fichaDo(id, '/addresses'), signal),
  addAddress: (id: string, input: PolicyholderAddressInput) =>
    api.post<PolicyholderAddressEntry>(fichaDo(id, '/addresses'), input),
  updateAddress: (id: string, itemId: string, input: PolicyholderAddressInput) =>
    api.patch<PolicyholderAddressEntry>(fichaDo(id, `/addresses/${itemId}`), input),
  removeAddress: (id: string, itemId: string) =>
    api.delete<void>(fichaDo(id, `/addresses/${itemId}`)),

  contacts: (id: string, signal?: AbortSignal) =>
    api.get<PolicyholderContact[]>(fichaDo(id, '/contacts'), signal),
  addContact: (id: string, input: { phone: string; is_main?: boolean }) =>
    api.post<PolicyholderContact>(fichaDo(id, '/contacts'), input),
  updateContact: (
    id: string,
    itemId: string,
    input: { phone?: string; is_main?: boolean },
  ) => api.patch<PolicyholderContact>(fichaDo(id, `/contacts/${itemId}`), input),
  removeContact: (id: string, itemId: string) =>
    api.delete<void>(fichaDo(id, `/contacts/${itemId}`)),

  economicGroup: (id: string, groupId: string, signal?: AbortSignal) =>
    api.get<EconomicGroupDetail>(fichaDo(id, `/economic-groups/${groupId}`), signal),
};

// --- Tomador complementar: regras executadas e historico (P-TOM-C2) ------------

/** Tipo da execucao de regras do tomador (Regras Executadas do OnPoint). */
export type RuleExecutionKind =
  'pre_cadastro' | 'motor_credito' | 'grupo_economico' | 'ccg';

/** Uma execucao de regras do tomador, do reprocessamento ou do motor pela operacao. */
export interface RuleExecution {
  id: string;
  run_kind: RuleExecutionKind;
  status: 'executada' | 'sem_regra_aprovada' | 'nao_executada';
  origin: 'reprocessamento' | 'operacao';
  reprocessing_id: string | null;
  executed_at: string;
  is_last: boolean;
  success: boolean | null;
  fatal: boolean | null;
  warning: boolean | null;
  manual_approval_enabled: boolean | null;
  pending_rules: string[];
  note: string;
  result: Record<string, unknown>;
  operation_id: string | null;
  engine_run: EngineRunSummary | null;
  requested_label: string;
}

/** Regra avaliada, no formato do OnPoint e com o que o motor registra. */
export interface RuleExecutionRule {
  rule_name: string;
  formula_id: string;
  success: boolean;
  warning: boolean;
  fatal: boolean;
  messages: string[];
  user_option: boolean | null;
  required_document: boolean;
  force_required_documents: boolean | null;
  action: string;
  effective: string;
  homologated: boolean;
  shadow: boolean;
}

export interface RuleExecutionDetail extends RuleExecution {
  rules: RuleExecutionRule[];
}

export interface ReprocessingResult {
  reprocessing_id: string;
  executions: RuleExecution[];
}

export interface ReprocessingInput {
  run_kinds: RuleExecutionKind[];
  operation_id: string | null;
}

/** Mudanca de um grupo no evento do historico de Limites e Taxas. */
export interface LimitHistoryItem {
  modality_group: string;
  previous_approved: Money | null;
  new_approved: Money;
  previous_used: Money | null;
  new_used: Money;
  new_available: Money;
  previous_public_rate_bps: number | null;
  new_public_rate_bps: number | null;
  previous_private_rate_bps: number | null;
  new_private_rate_bps: number | null;
  previous_engine_limit: Money | null;
  new_engine_limit: Money | null;
  /** C-LIM: validade do limite antes e depois. */
  previous_valid_until?: string | null;
  new_valid_until?: string | null;
  changed_manually: boolean;
}

/** Evento do historico de Limites e Taxas, manual ou automatico. */
export interface LimitHistoryEntry {
  id: string;
  event: 'manual' | 'motor' | 'carga';
  event_label: string;
  changed_manually: boolean;
  rule_execution_id: string | null;
  /** Motivo de quem mudou o limite. Vazio para a corretora. */
  reason?: string;
  actor_label: string;
  created_at: string;
  items: LimitHistoryItem[];
  /** PM-09: valores ocultos a corretora. */
  hidden_from_broker?: boolean;
}

/** Rotas das regras executadas, do reprocessamento e do evento do historico. */
export const policyholderRules = {
  executions: (
    id: string,
    filtro: { run_kind?: RuleExecutionKind; last?: boolean } = {},
    signal?: AbortSignal,
  ) =>
    api.get<RuleExecution[]>(
      fichaDo(
        id,
        `/rule-executions${query({
          run_kind: filtro.run_kind,
          last: filtro.last === undefined ? undefined : String(filtro.last),
        })}`,
      ),
      signal,
    ),
  execution: (id: string, executionId: string, signal?: AbortSignal) =>
    api.get<RuleExecutionDetail>(
      fichaDo(id, `/rule-executions/${executionId}`),
      signal,
    ),
  reprocess: (id: string, input: ReprocessingInput) =>
    api.post<ReprocessingResult>(fichaDo(id, '/reprocessing'), input),
  limitHistoryEntry: (id: string, historyId: string, signal?: AbortSignal) =>
    api.get<LimitHistoryEntry>(
      fichaDo(id, `/credit-limits/history/${historyId}`),
      signal,
    ),
};

// --- Grupo economico como entidade e nomeacao do corretor (P-TOM-C5) -----------

/** Um tomador da empresa no grupo, com os limites dele mesmo (dado do tomador). */
export interface EconomicGroupMemberRow {
  participation_id?: string;
  policyholder: { id: string; label: string };
  document: string;
  participation_bps: number;
  afiancado_holding?: boolean;
  group_limit: Money | null;
  joined_at?: string;
  granted_limit?: Money;
  used_limit?: Money;
}

/** Linha de ModalityGroupLimits: o limite do grupo no grupo de modalidades. */
export interface EconomicGroupModalityLimit {
  modality_group: string;
  limit: Money | null;
  changed_manually: boolean;
  updated_label: string;
  updated_at: string | null;
  /** Totais do grupo no grupo de modalidades: nulos ate LIM-005 e CRE-06. */
  granted_limit: Money | null;
  used_limit: Money | null;
  available_limit: Money | null;
}

/**
 * O grupo economico como entidade. `limit` e informado por quem tem a alcada;
 * concedido, utilizado e disponivel do grupo saem nulos ate a metrica de consumo do
 * grupo ser aprovada (`pending_rules`: LIM-002, LIM-005, G1 e CRE-06).
 */
export interface EconomicGroupEntity {
  id: string;
  document: string;
  name: string;
  limit: Money | null;
  limit_changed_manually: boolean;
  limit_updated_label: string;
  limit_updated_at: string | null;
  /** C-LIM: validade do limite do grupo (AAAA-MM-DD) e a ultima mudanca do valor. */
  limit_valid_until?: string | null;
  limit_approved_at?: string | null;
  created_at: string;
  updated_at: string;
  granted_limit: Money | null;
  used_limit: Money | null;
  available_limit: Money | null;
  modality_limits: EconomicGroupModalityLimit[];
  members: EconomicGroupMemberRow[];
  head: { policyholder_id: string; legal_name: string; created_at: string } | null;
  pending_rules: string[];
}

/** HasChangeEconomicGroup do OnPoint, no envelope da lista. */
export interface EconomicGroupChangeCheck {
  can_change_group: boolean;
  change_blocked_reason: string;
}

/** Rotas do grupo economico como entidade. */
export const economicGroups = {
  /** As participacoes do tomador com o grupo de cada uma e o que a sessao pode trocar. */
  participations: async (id: string, signal?: AbortSignal) => {
    const pagina = await requestPage<EconomicGroupDetail>(
      fichaDo(id, '/economic-groups'),
      signal,
    );
    const motivo = pagina.meta['change_blocked_reason'];
    return {
      items: pagina.items,
      change: {
        can_change_group: pagina.meta['can_change_group'] === true,
        change_blocked_reason: typeof motivo === 'string' ? motivo : '',
      } satisfies EconomicGroupChangeCheck,
    };
  },
  get: (groupId: string, signal?: AbortSignal) =>
    api.get<EconomicGroupEntity>(`/api/v1/economic-groups/${groupId}`, signal),
  update: (
    groupId: string,
    input: { name?: string; limit?: Money | null; limit_valid_until?: string },
  ) => api.patch<EconomicGroupEntity>(`/api/v1/economic-groups/${groupId}`, input),
  setModalityLimit: (groupId: string, modalityGroup: string, limit: Money | null) =>
    api.put<EconomicGroupEntity>(
      `/api/v1/economic-groups/${groupId}/modality-limits/${modalityGroup}`,
      { limit },
    ),
};

/** Modo da empresa: pedido de nomeacao (OnPoint) ou troca direta no cadastro. */
export type NominationMode = 'pedido' | 'troca_direta';

export interface NominationSettings {
  mode: NominationMode;
  request_enabled: boolean;
  updated_label?: string;
  updated_at?: string | null;
}

export type NominationKind = 'pedido' | 'seguradora' | 'remocao';
export type NominationStatus = 'pendente' | 'aprovada' | 'recusada' | 'cancelada';

/** Pedido de nomeacao, nomeacao pela seguradora ou pedido de remocao. */
export interface Nomination {
  id: string;
  policyholder_id: string;
  broker: { id: string; label: string };
  broker_document: string;
  kind: NominationKind;
  status: NominationStatus;
  valid_until: string | null;
  instrument: string;
  letter_document_id: string | null;
  target_nomination_id: string | null;
  request_note: string;
  requested_label: string;
  requested_by_broker: boolean;
  requested_at: string;
  decision_note: string;
  decline_reason_id: string | null;
  decline_reason: string;
  decided_label: string;
  decided_at: string | null;
  ended_at: string | null;
  ended_reason: string;
  current: boolean;
}

/** A secao Corretor do tomador, com os indicadores do GetPolicyHolder do OnPoint. */
export interface NominationList {
  items: Nomination[];
  current: Nomination | null;
  settings: NominationSettings;
  nomination_pending_response: boolean;
  has_pending_nomination: boolean;
  nominated_to_this_broker: boolean;
  nominated_to_other_broker: boolean;
  last_nomination_request_id: string | null;
  pending_rule?: string;
}

export interface NominationInput {
  broker_id?: string;
  kind?: 'pedido' | 'seguradora';
  valid_until?: string;
  instrument?: string;
  letter_document_id?: string;
  note?: string;
}

/** Rotas da nomeacao do corretor. */
export const nominations = {
  settings: (signal?: AbortSignal) =>
    api.get<NominationSettings>('/api/v1/policyholders/nomination-settings', signal),
  saveSettings: (mode: NominationMode) =>
    api.put<NominationSettings>('/api/v1/policyholders/nomination-settings', { mode }),
  list: (id: string, signal?: AbortSignal) =>
    api.get<NominationList>(fichaDo(id, '/nominations'), signal),
  request: (id: string, input: NominationInput) =>
    api.post<Nomination>(fichaDo(id, '/nominations'), input),
  removal: (id: string, nominationId: string, note: string) =>
    api.post<Nomination>(fichaDo(id, `/nominations/${nominationId}/removal`), { note }),
  decide: (
    id: string,
    nominationId: string,
    input: {
      decision: 'aprovar' | 'recusar';
      decline_reason_id?: string;
      note?: string;
    },
  ) =>
    api.post<Nomination>(fichaDo(id, `/nominations/${nominationId}/decision`), input),
  cancel: (id: string, nominationId: string, note: string) =>
    api.post<Nomination>(fichaDo(id, `/nominations/${nominationId}/cancellation`), {
      note,
    }),
};

// --- Analises do proprio tomador (P-TOM-C4) --------------------------------------------
// Ver docs/ADR/0058-analises-do-tomador.md. A analise registra o
// processo do tomador fora da operacao e a decisao de quem tem a alcada; nada e decidido
// sozinho, e a decisao nao muda limite, cadastro nem bloqueio.

export type PolicyholderReviewKind =
  'cadastro' | 'credito' | 'pep' | 'grupo_economico' | 'revisao_limite';
export type PolicyholderReviewStatus =
  'pendente' | 'aprovada' | 'recusada' | 'cancelada';
export type PolicyholderReviewDecision = 'aprovar' | 'recusar' | 'cancelar';

/** Documento pedido na analise (RequestDocuments do OnPoint). */
export interface PolicyholderReviewDocument {
  id: string;
  title: string;
  group_name: string;
  status: 'pendente' | 'recebido' | 'dispensado';
  required: boolean;
  requested_at?: string | null;
}

/** O cartao da esteira em que a analise corre. */
export interface PolicyholderReviewCard {
  id: string;
  pipe_key: string;
  pipe_name: string;
  step_key: string;
  step_name: string;
  step_type: string;
  is_closed: boolean;
  /** Verdadeiro quando a analise abriu o cartao; falso, quando apontou para um aberto. */
  opened: boolean;
}

export interface PolicyholderReview {
  id: string;
  policyholder_id: string;
  kind: PolicyholderReviewKind;
  kind_label: string;
  status: PolicyholderReviewStatus;
  reason: string;
  card: PolicyholderReviewCard | null;
  limit_review: {
    id: string;
    status: string;
    modality_group: string;
    requested_amount: Money | null;
  } | null;
  opened_label: string;
  opened_at: string;
  decision_note: string;
  decline_reason_id: string | null;
  decline_reason: string;
  decided_label: string;
  decided_at: string | null;
  documents: PolicyholderReviewDocument[];
  /** Verdadeiro quando a decisao e de quem tem a alcada; falso so na do motor. */
  manual_decision: boolean;
  /** A aprovacao e do motor de credito (ADR 0090). */
  decided_by_engine?: boolean;
  /** A execucao do motor que aprovou ou que abriu a analise. */
  engine_run_id?: string | null;
  engine_parameter_version?: number | null;
  /** Sempre vazia: a decisao nao muda o tomador. */
  changes: string[];
  /** Leitura da corretora, sem as notas internas da seguradora. */
  redacted: boolean;
}

/** O envelope da lista: GetPolicyHolderTimelineInfo e os indicadores do GetPolicyHolder. */
export interface PolicyholderReviewMeta {
  has_pending_documents: boolean;
  request_documents: PolicyholderReviewDocument[];
  economic_group_review_in_progress: boolean;
  credit_review_in_progress: boolean;
  pep_review_approved: boolean;
  has_card_in_analysis: boolean;
  pending_kinds: string[];
  enabled_kinds: string[];
  can_open: boolean;
  open_kinds: PolicyholderReviewKind[];
  pending_rules: string[];
}

export interface PolicyholderReviewEvent {
  id: string;
  kind: 'abertura' | 'anotacao' | 'alteracao' | 'cartao' | 'decisao' | 'cancelamento';
  status: PolicyholderReviewStatus;
  note: string;
  broker_visible: boolean;
  detail: Record<string, unknown>;
  actor_label: string;
  created_at: string;
}

export interface PolicyholderReviewInput {
  kind: PolicyholderReviewKind;
  reason?: string;
  limit_review_id?: string;
}

export interface PolicyholderReviewUpdate {
  reason?: string;
  note?: string;
  note_visible_to_broker?: boolean;
  decision?: PolicyholderReviewDecision;
  decline_reason_id?: string;
  /** Limite novo da revisao de limite aprovada. Ausente: o limite nao muda. */
  approved_limit?: Money;
}

export interface PolicyholderReviewSetting {
  kind: PolicyholderReviewKind;
  kind_label: string;
  enabled: boolean;
  pipe_key: string | null;
  default_pipe_key: string | null;
  stored: boolean;
  updated_label?: string;
  updated_at?: string | null;
}

export interface PolicyholderReviewSettings {
  items: PolicyholderReviewSetting[];
  pipes: Array<{ key: string; name: string }>;
}

function metaDaAnalise(meta: Record<string, unknown>): PolicyholderReviewMeta {
  const lista = <T>(valor: unknown): T[] =>
    Array.isArray(valor) ? (valor as T[]) : [];
  return {
    has_pending_documents: meta['has_pending_documents'] === true,
    request_documents: lista<PolicyholderReviewDocument>(meta['request_documents']),
    economic_group_review_in_progress:
      meta['economic_group_review_in_progress'] === true,
    credit_review_in_progress: meta['credit_review_in_progress'] === true,
    pep_review_approved: meta['pep_review_approved'] === true,
    has_card_in_analysis: meta['has_card_in_analysis'] === true,
    pending_kinds: lista<string>(meta['pending_kinds']),
    enabled_kinds: lista<string>(meta['enabled_kinds']),
    can_open: meta['can_open'] === true,
    open_kinds: lista<PolicyholderReviewKind>(meta['open_kinds']),
    pending_rules: lista<string>(meta['pending_rules']),
  };
}

/** Rotas das analises do tomador. */
export const policyholderReviews = {
  list: async (
    id: string,
    filters: ListParams & { kind?: string; status?: string } = {},
    signal?: AbortSignal,
  ) => {
    const pagina = await requestPage<PolicyholderReview>(
      fichaDo(id, `/reviews${query(filters)}`),
      signal,
    );
    return {
      items: pagina.items,
      total: pagina.total,
      meta: metaDaAnalise(pagina.meta),
    };
  },
  open: (id: string, input: PolicyholderReviewInput) =>
    api.post<PolicyholderReview>(fichaDo(id, '/reviews'), input),
  update: (id: string, reviewId: string, input: PolicyholderReviewUpdate) =>
    api.patch<PolicyholderReview>(fichaDo(id, `/reviews/${reviewId}`), input),
  events: async (id: string, reviewId: string, signal?: AbortSignal) =>
    (
      await requestPage<PolicyholderReviewEvent>(
        fichaDo(id, `/reviews/${reviewId}/events`),
        signal,
      )
    ).items,
  settings: (signal?: AbortSignal) =>
    api.get<PolicyholderReviewSettings>(
      '/api/v1/policyholders/review-settings',
      signal,
    ),
  saveSettings: (
    items: Array<{
      kind: PolicyholderReviewKind;
      enabled: boolean;
      pipe_key: string | null;
    }>,
  ) =>
    api.put<PolicyholderReviewSettings>('/api/v1/policyholders/review-settings', {
      items,
    }),
};

// --- Dados do leiloeiro do tomador pessoa fisica (P-NCT-3) ---------------------------

/**
 * Se o tomador pessoa fisica e leiloeiro (o cartao "Dados do Leiloeiro" do OnPoint) e,
 * sendo, a UF da junta comercial da matricula e o numero da matricula. Ver
 * docs/ADR/0066-cotacao-pessoa-fisica-e-leiloeiro.md.
 */
export interface PolicyholderAuctioneer {
  policyholder_id: string;
  is_individual: boolean;
  is_auctioneer: boolean;
  auctioneer: {
    registry_state: string;
    registration_number: string;
    created_label: string;
    updated_label: string;
    created_at: string;
    updated_at: string;
  } | null;
}

export interface PolicyholderAuctioneerInput {
  registry_state: string;
  registration_number?: string;
}

/** Rotas dos dados do leiloeiro, com as permissoes e o escopo do cadastro do tomador. */
export const policyholderAuctioneers = {
  get: (id: string, signal?: AbortSignal) =>
    api.get<PolicyholderAuctioneer>(`/api/v1/policyholders/${id}/auctioneer`, signal),
  set: (id: string, input: PolicyholderAuctioneerInput) =>
    api.put<PolicyholderAuctioneer>(`/api/v1/policyholders/${id}/auctioneer`, input),
  remove: (id: string) => api.delete<void>(`/api/v1/policyholders/${id}/auctioneer`),
};
